import { AreaPageListing, AreaPageListing_ListingType, Provider, AreaPageListing_Status } from '@zoocasa/go-search';
import { capitalizeWords } from '@zoocasa/node-kit/strings/capitalize';
import { AgreementTypes } from 'types/agreement-storage-types';
import { cleanDashedString } from 'data/addresses';
import { isVOWProvider } from 'utils/compliance';

import type { ListingCardData } from 'components/listing-card/listing-card';

export class AreaPageListingCardData implements ListingCardData {
  constructor(private listing: AreaPageListing) { }

  get id() {
    return this.listing.id;
  }

  get imageUrl() {
    if (this.hasImageRootStorageKey()) {
      if (this.hasImageSource()) {
        return this.listing.imageRootStorageKey;
      }
      return `https://cdn0.zoocasa.com/${this.listing.imageRootStorageKey}.jpg`;
    }
    return '';
  }

  get slug() {
    return this.listing.slug;
  }

  get isImageReady() {
    return this.hasImageRootStorageKey();
  }

  get imageDesc() {
    return this.listing.address;
  }

  get displayAddress() {
    return this.listing.displayAddress?.internetAddressDisplay !== undefined ?
      Boolean(this.listing.displayAddress.internetAddressDisplay) : true;
  }

  get addressPathWithFallback() {
    if (!this.displayAddress) {
      return this.listing.addressUrlAbsolutePath;
    }
    return cleanDashedString(this.listing.addressUrlAbsolutePath || this.listing.listingUrlAbsolutePath);
  }

  get isCrea() {
    return this.listing.providerId == Provider.PROVIDER_CREA_DDF;
  }

  get isItsoVow() {
    return this.listing.providerId == Provider.PROVIDER_ITSO_VOW;
  }

  get isPillar9Vow() {
    return this.listing.providerId == Provider.PROVIDER_PILLAR9_VOW;
  }

  get isRebgvVow() {
    return this.listing.providerId == Provider.PROVIDER_REBGV_VOW;
  }

  get isTrebIdxInactive() {
    return this.listing.providerId === Provider.PROVIDER_TREB_IDX && !this.isAvailable;
  }

  get isStellarMls() {
    return this.listing.providerId === Provider.PROVIDER_STELLAR_MLS_IDX;
  }

  get agreementType() {
    if (this.isPillar9Vow) {
      return AgreementTypes.PILLAR9_VOW;
    } else if (this.isRebgvVow) {
      return AgreementTypes.REBGV_VOW;
    } else if (this.isItsoVow) {
      return AgreementTypes.ITSO_VOW;
    } else if (this.isCrea) {
      return AgreementTypes.CREA;
    }
  }

  get isSignInAndTermsOfUseRequired() {
    return this.listing.termsOfUseRequired ? true : false;
  }

  get mlsNum() {
    return this.listing.mls;
  }

  get brokerage() {
    return this.listing.brokerage;
  }

  public get providerId() {
    return this.listing.providerId;
  }

  public get city() {
    return this.listing.subDivision;
  }

  public get province() {
    return this.listing.province;
  }

  public get isPrivate() {
    return this.isVow;
  }

  get isAvailable() {
    return this.listing.statusId === AreaPageListing_Status.Available;
  }

  get isSold() {
    return this.listing.statusId === AreaPageListing_Status.NotAvailableSold;
  }

  get isSoldWithoutSoldPrice() {
    return this.listing.statusId === AreaPageListing_Status.NotAvailableSold && !this.soldPrice;
  }

  get statusId() {
    return this.listing.statusId;
  }

  public get isVow() {
    return isVOWProvider(this.listing.providerId);
  }

  get position() {
    return null;
  }

  getStreet = (isAuthenticated: boolean) => {
    if (!this.displayAddress) {
      return 'Address not available';
    }
    return capitalizeWords(!isAuthenticated && this.isPrivate ? this.listing?.streetName : this.seoStreet);
  };

  get isTreb() {
    return !this.isCrea;
  }

  get soldPrice() {
    return this.listing.SoldPrice > 0 ? this.listing.SoldPrice : null;
  }

  get soldAt() {
    return this.listing.soldAt || null;
  }

  get price() {
    return this.listing.price;
  }

  get addedAt() {
    return this.listing.createdAt || null;
  }

  get updatedOn() {
    return this.listing.updatedOn || null;
  }

  get expiredAt() {
    return this.listing.expiredAt || null;
  }

  get bedrooms() {
    return this.listing.bedrooms;
  }

  get bathrooms() {
    return this.listing.bathrooms;
  }

  get avgSqft() {
    let avgSqft;
    if (this.listing.squareFootage) {
      const { gt, gte, lt, lte } = this.listing.squareFootage;
      const min = lt || lte;
      const max = gt || gte;
      if (min && max) {
        avgSqft = Math.ceil((min + max) / 2);
      } else if (min) {
        avgSqft = min;
      } else if (max) {
        avgSqft = max;
      }
    }
    return avgSqft;
  }

  get sqftLabel() {
    let sqftLabel = '';
    if (this.listing.squareFootage) {
      const { gt, gte, lt, lte } = this.listing.squareFootage;
      const min = gt || gte;
      const max = lt || lte;
      let sqft = 'N/A';

      // Note: if max sqft is 499 always show 0-499, for anything else show min-max or just min or max when only one available
      sqft = max === 499 ? '0-499' : [min, max].filter(item => item).join('–');

      sqftLabel = `${!sqft?.trim()?.length ? 'N/A' : sqft.trim()} sqft`;
    }
    return sqftLabel;
  }

  get statusLabel() {
    let lastStatusLabel: string | null = null;
    if (this.listing.lastStatus) {
      const lastStatusMappings: Record<string, string> = {
        Bom: 'Back on Market',
        Dft: 'Deal Fell Through',
        Exp: 'Expired',
        Ext: 'Extended',
        Lc: 'Leased Conditionally',
        Lsd: 'Leased',
        New: 'New',
        Pc: 'Price Change',
        Sc: 'Sold Conditionally',
        Sld: 'Sold',
        Sus: 'Suspended',
        Ter: 'Terminated',
        Pen: 'Pending',
      };
      lastStatusLabel = lastStatusMappings[this.listing.lastStatus];
    }
    if (lastStatusLabel && lastStatusLabel !== 'New') {
      return lastStatusLabel;
    } else if (this.isAvailable) {
      return this.listing.listingType === AreaPageListing_ListingType.rent ? 'For Rent' : 'For Sale';
    } else if (this.isSold) {
      return this.listing.listingType === AreaPageListing_ListingType.rent ? 'Leased' : 'Sold';
    // } else if (this.isPending) {
    //   return 'Pending';
    } else {
      return 'Inactive';
    }
  }

  private hasImageRootStorageKey() {
    return this.listing?.imageRootStorageKey.length > 0;
  }

  private hasImageSource() {
    return this.listing?.imageSource == 'exp';
  }

  private get seoStreet() {
    const unitNumber = this.listing?.unit;
    const streetAddress = this.streetAddress;
    const seoStreet = unitNumber?.length > 0 ? `${unitNumber} - ${streetAddress}` : streetAddress;
    return seoStreet;
  }

  private get streetAddress() {
    const { streetNumber, streetName } = this.listing;
    return streetNumber ? `${streetNumber} ${streetName}` : streetName;
  }

  get isRental() {
    return this.listing.listingType === AreaPageListing_ListingType.rent;
  }

  get standardStatus() {
    return this.listing.standardStatus;
  }

  toJSON(rawListing: AreaPageListing) {
    return {
      // Core identification
      id: this.id ?? null,
      slug: this.slug ?? null,
      
      // Image properties
      imageUrl: this.imageUrl ?? null,
      imageDesc: this.imageDesc ?? null,
      isImageReady: this.isImageReady ?? null,
      
      // Address properties
      addressPathWithFallback: this.addressPathWithFallback ?? null,
      city: this.city ?? null,
      province: this.province ?? null,
      displayAddress: this.displayAddress ?? null,
      streetName: rawListing.streetName ?? null,
      seoStreet: rawListing.address ?? null,
      position: this.position ?? null,
      
      // Provider properties
      providerId: this.providerId ?? null,
      mlsNum: this.mlsNum ?? null,
      brokerage: this.brokerage ?? null,
      
      // Compliance properties (legacy format)
      isCrea: this.isCrea ?? null,
      isItsoVow: this.isItsoVow ?? null,
      isPillar9Vow: this.isPillar9Vow ?? null,
      isRebgvVow: this.isRebgvVow ?? null,
      isTrebIdxInactive: this.isTrebIdxInactive ?? null,
      isStellarMls: this.isStellarMls ?? null,
      agreementType: this.agreementType ?? null,
      isSignInAndTermsOfUseRequired: this.isSignInAndTermsOfUseRequired ?? null,
      isPrivate: this.isPrivate ?? null,
      isVow: this.isVow ?? null,
      isTreb: this.isTreb ?? null,
      
      // Status properties
      statusId: this.statusId ?? null,
      statusLabel: this.statusLabel ?? null,
      isAvailable: this.isAvailable ?? null,
      isSold: this.isSold ?? null,
      isSoldWithoutSoldPrice: this.isSoldWithoutSoldPrice ?? null,
      isRental: this.isRental ?? null,
      standardStatus: this.standardStatus ?? null,
      
      // Price properties
      price: this.price ?? null,
      soldPrice: this.soldPrice ?? null,
      
      // Date properties (convert Date to ISO string for SSR serialization)
      addedAt: (this.addedAt instanceof Date ? this.addedAt.toISOString() : this.addedAt) ?? null,
      updatedOn: (this.updatedOn instanceof Date ? this.updatedOn.toISOString() : this.updatedOn) ?? null,
      soldAt: (this.soldAt instanceof Date ? this.soldAt.toISOString() : this.soldAt) ?? null,
      expiredAt: (this.expiredAt instanceof Date ? this.expiredAt.toISOString() : this.expiredAt) ?? null,
      
      // Property details
      bedrooms: this.bedrooms ?? null,
      bathrooms: this.bathrooms ?? null,
      avgSqft: this.avgSqft ?? null,
      sqftLabel: this.sqftLabel ?? null,
    };
  }
}